import { useState, useEffect, createContext, useContext, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate, Link } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Auth Context ──
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("zybercure_token"));

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      const headers = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const resp = await axios.get(`${API}/auth/me`, {
        headers,
        withCredentials: true,
      });
      setUser(resp.data);
    } catch {
      setUser(null);
      localStorage.removeItem("zybercure_token");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (userData, authToken) => {
    setUser(userData);
    if (authToken) {
      setToken(authToken);
      localStorage.setItem("zybercure_token", authToken);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    setUser(null);
    setToken(null);
    localStorage.removeItem("zybercure_token");
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, token }}>
      {children}
    </AuthContext.Provider>
  );
}

// ── Protected Route ──
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}

// ── Founder-only Route ──
function FounderRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "founder") return <Navigate to="/dashboard" replace />;
  return children;
}

// ── Auth Callback (Google OAuth) ──
function AuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.substring(1)).get("session_id");
    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const resp = await axios.get(`${API}/auth/session?session_id=${sessionId}`, {
          withCredentials: true,
        });
        login(resp.data);
        navigate("/dashboard", { replace: true, state: { user: resp.data } });
      } catch {
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, login]);

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

// ── Lazy Imports ──
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import BreachCheckerPage from "@/pages/BreachCheckerPage";
import PasswordAnalyzerPage from "@/pages/PasswordAnalyzerPage";
import ThreatFeedPage from "@/pages/ThreatFeedPage";
import EducationPage from "@/pages/EducationPage";
import PricingPage from "@/pages/PricingPage";
import ChatPage from "@/pages/ChatPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import MarketplacePage from "@/pages/MarketplacePage";
import AgentBuilderPage from "@/pages/AgentBuilderPage";
import FoundersCockpitPage from "@/pages/FoundersCockpitPage";
import AgentRuntimePage from '@/pages/AgentRuntimePage';
import PatentDraftPage from '@/pages/PatentDraftPage';

function AppRouter() {
  const location = useLocation();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/education" element={<EducationPage />} />
      <Route path="/runtime" element={<ProtectedRoute><AgentRuntimePage /></ProtectedRoute>} />
      <Route path="/patent-draft" element={<FounderRoute><PatentDraftPage /></FounderRoute>} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tools/breach-checker"
        element={
          <ProtectedRoute>
            <BreachCheckerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tools/password-analyzer"
        element={
          <ProtectedRoute>
            <PasswordAnalyzerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/threats"
        element={
          <ProtectedRoute>
            <ThreatFeedPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <ChatPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketplace"
        element={
          <ProtectedRoute>
            <MarketplacePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketplace/build"
        element={
          <ProtectedRoute>
            <AgentBuilderPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment/success"
        element={
          <ProtectedRoute>
            <PaymentSuccessPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/founders-cockpit"
        element={
          <FounderRoute>
            <FoundersCockpitPage />
          </FounderRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
export { API, BACKEND_URL };
